import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';

import useWindowSize from '@hooks/useWindowSize';
import { OfferOption, SelectedOffer } from '@utils/backend-api/subscribe';
import { WIDTH_SM } from '@utils/constants';

import CorporateInquiry from './CorporateInquery';
import HorizontalOffer from './HorizontalOffer';
import VerticalOffer from './VerticalOffer';

interface ContentProps {
  defaultSelectedOffer: SelectedOffer;
  offers: OfferOption[];
}

const Content = ({ defaultSelectedOffer, offers }: ContentProps) => {
  const [selectedOffer, setSelectedOffer] = useState(defaultSelectedOffer);
  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth < WIDTH_SM;

  const verticalOffers = useMemo(
    () => offers.filter((offer) => offer.isPro || (offer.isAnnual && !offer.isYp)),
    [offers]
  );

  const horizontalOffers = useMemo(
    () =>
      offers
        .filter((offer) => offer.isYp || offer.isMonthly)
        .sort((a, b) => (a.isYp ? -1 : b.isYp ? 1 : 0)),
    [offers]
  );

  const [midPoint, setMidPoint] = useState('50%');

  useEffect(() => {
    if (defaultSelectedOffer.isYp || defaultSelectedOffer.isMonthly) {
      setSelectedOffer(verticalOffers[0]);
    }
  }, [defaultSelectedOffer, verticalOffers]);

  useEffect(() => {
    if (isMobile && verticalOffers.length >= 2) {
      const first = document.getElementById(verticalOffers[1].code);
      const second = document.getElementById(verticalOffers[0].code);

      if (!first || !second) return;

      let rafId: number;

      const updateMidpoint = () => {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          const firstBottom = first.offsetTop + first.offsetHeight;
          const secondTop = second.offsetTop;
          const middleY = (firstBottom + secondTop) / 2;
          setMidPoint(`${middleY}px`);
        });
      };

      // Observe size changes
      const resizeObserver = new ResizeObserver(updateMidpoint);
      resizeObserver.observe(first);
      resizeObserver.observe(second);

      // Observe DOM mutations inside containers
      const mutationObserver = new MutationObserver(updateMidpoint);
      mutationObserver.observe(first, { childList: true, subtree: true });
      mutationObserver.observe(second, { childList: true, subtree: true });

      // Initial adjustment
      updateMidpoint();

      return () => {
        cancelAnimationFrame(rafId);
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      };
    }
  }, [isMobile, verticalOffers]);

  return (
    <div className="px-16 sm:px-28" data-testid="dual-subscribe-multiple-offers">
      <div className="md:max-w relative mx-auto w-full sm:max-w-[1040px] xl:max-w-[1130px]">
        <div className="mb-64 flex max-w-full flex-col-reverse flex-wrap sm:flex-row">
          <div
            className="absolute left-[50%] z-10 h-[25px] w-[25px] -translate-x-1/2 -translate-y-1/2 rotate-45 bg-grayscale-beige md:h-[35px] md:w-[35px]"
            style={isMobile ? { top: midPoint } : { top: '45px' }}
          ></div>
          {verticalOffers.map((offer, index) => (
            <VerticalOffer
              presentedOffers={offers}
              key={index}
              offer={offer}
              isOfferSelected={selectedOffer.code === offer.code}
              isMobile={isMobile}
              setSelectedOffer={setSelectedOffer}
              className={clsx(
                'border border-b border-l border-r border-solid border-grayscale-beige',
                {
                  'border-r-transparent': index % 2 === 0,
                  'border-l-transparent ': index % 2 !== 0,
                }
              )}
            />
          ))}
        </div>
        {horizontalOffers.length > 0 && (
          <div className="flex flex-col items-center justify-center">
            <div className="mb-16 font-suisse-screen text-headline-xxs">See more subscriptions</div>
            {horizontalOffers.map((offer, index) => (
              <div
                id={offer.code}
                key={index}
                className={clsx(
                  'relative flex w-full flex-wrap items-center justify-between gap-16 border border-l-0 border-r-0 border-t border-solid bg-white px-16 py-24 md:w-[836px] md:gap-90 md:px-48',
                  {
                    'border-b': index === horizontalOffers.length - 1,
                    'border-b-0': index !== horizontalOffers.length - 1,
                  }
                )}
              >
                <HorizontalOffer offer={offer} />
              </div>
            ))}
          </div>
        )}
        <CorporateInquiry />
      </div>
    </div>
  );
};

export default Content;
